import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { graphqlClient } from '../../services/graphqlClient';
import { buildSubmitProcessingPayload } from '../../services/apiPayloadBuilder';
import * as mutations from '../../graphql/mutations';
import { JobType } from '../../graphql/types';
import type {
  Document,
  File as FileType,
  Loan,
  SubmitDocumentsForProcessingInput,
  WorkflowJob,
} from '../../graphql/types';
import type { BAFormState } from './intakeBAModel';

/**
 * Data calls for BA Intake. They reuse the calls the legacy
 * `DocumentProcessingStep` makes: `listLoansByJob` / `listFilesByLoan` /
 * `listDocumentsByLoan` to load, `updateLoanInfo` to save the form, an S3 put
 * for each document, and `submitDocumentsForProcessing` + `submitJob` to
 * extract (with `isResubmission` once extraction has started, so only the files
 * added since then are picked up).
 */

const client = generateClient();

export interface IntakeBAData {
  tenantId: string;
  loan: Loan | null;
  files: FileType[];
  documents: Document[];
}

export async function getTenantId(): Promise<string> {
  const session = await fetchAuthSession();
  return (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
}

export async function loadIntakeData(jobId: string): Promise<IntakeBAData> {
  const tenantId = await getTenantId();
  const loans = await graphqlClient.queries.listLoansByJob(jobId, tenantId);
  const loan = loans.items[0] ?? null;
  if (!loan) return { tenantId, loan: null, files: [], documents: [] };
  const [files, documents] = await Promise.all([
    graphqlClient.queries.listFilesByLoan(loan.loanId, jobId, tenantId),
    graphqlClient.queries.listDocumentsByLoan(loan.loanId, jobId, tenantId),
  ]);
  return { tenantId, loan, files: files.items, documents: documents.items };
}

/** A BA job has one loan. Create it when the job was made without one. */
export async function ensureLoan(jobId: string, existing: Loan | null): Promise<Loan> {
  if (existing) return existing;
  const session = await fetchAuthSession();
  const payload = session.tokens?.idToken?.payload ?? {};
  const tenantId = (payload['custom:tenantId'] as string) || 'default-tenant';
  const username = (payload['cognito:username'] as string) || 'unknown';
  const cognitoSub = (payload['sub'] as string) || 'unknown';
  return graphqlClient.mutations.createLoan({
    loanId: `loan-${Date.now()}`,
    jobId,
    tenantId,
    username,
    cognitoSub,
  });
}

/**
 * Saves the fields the Loan record stores. Blank fields are sent as `undefined`
 * (left unchanged), as the legacy loan editor does.
 */
export async function saveLoanFields(
  loan: Pick<Loan, 'loanId' | 'jobId'>,
  tenantId: string,
  form: BAFormState
): Promise<void> {
  await client.graphql({
    query: mutations.updateLoanInfo,
    variables: {
      loanId: loan.loanId,
      jobId: loan.jobId,
      tenantId,
      loanNumber: form.loanNumber.trim() || undefined,
      borrowerName: form.borrowerName.trim() || undefined,
      propertyAddress: form.propertyAddress.trim() || undefined,
      loanAmount: form.loanAmount.trim() || undefined,
    },
  });
}

/** Where the job's uploads go: the `uploadLocation` prefix, as the legacy step derives it. */
export function uploadPrefixFor(job: Pick<WorkflowJob, 'jobId' | 'uploadLocation'>, userId: string): string {
  if (job.uploadLocation) return job.uploadLocation.replace(/^s3:\/\/[^/]+\//, '');
  return `default-tenant/${userId}/${job.jobId}/`;
}

export async function uploadDocument(
  file: File,
  targetName: string,
  loanId: string,
  job: Pick<WorkflowJob, 'jobId' | 'uploadLocation'>,
  bucket: string
): Promise<void> {
  const session = await fetchAuthSession();
  const creds = session.credentials;
  if (!creds) throw new Error('No credentials available');
  const userId = (session.tokens?.idToken?.payload['cognito:username'] as string) || 'unknown';
  const s3 = new S3Client({
    region: import.meta.env.VITE_AWS_REGION || 'us-west-2',
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    },
    requestChecksumCalculation: 'WHEN_REQUIRED',
    responseChecksumValidation: 'WHEN_REQUIRED',
  });
  const key = `${uploadPrefixFor(job, userId)}${loanId}/${targetName}`;
  await s3.send(
    new PutObjectCommand({
      Bucket: bucket,
      Key: key,
      Body: new Uint8Array(await file.arrayBuffer()),
      ContentType: file.type,
    })
  );
}

/**
 * Sends the job to extraction. With `isResubmission` the pipeline picks up only
 * the S3 objects it has not processed yet, so documents added later are the only
 * ones extracted (Requirement 5.6).
 */
export async function submitForExtraction(
  job: Pick<WorkflowJob, 'jobId'>,
  isResubmission: boolean
): Promise<WorkflowJob | null> {
  const tenantId = await getTenantId();
  const payload: SubmitDocumentsForProcessingInput = buildSubmitProcessingPayload(
    job.jobId,
    tenantId,
    JobType.BA
  );
  const result = await client.graphql({
    query: mutations.submitDocumentsForProcessing,
    variables: { input: payload },
  });
  const updated = ((result as { data?: { submitDocumentsForProcessing?: WorkflowJob } }).data
    ?.submitDocumentsForProcessing ?? null) as WorkflowJob | null;
  await client.graphql({
    query: mutations.submitJob,
    variables: { input: isResubmission ? { jobId: job.jobId, isResubmission: true } : { jobId: job.jobId } },
  });
  return updated;
}
